import { ArrowForward, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/config';
import { useLocalization } from '@/contexts/LocalizationContext';

interface RegisterSuccessProps {
  onContinue: () => void;
}

const SuccessMessageContent = ({
  handleContinue,
}: {
  handleContinue: () => void;
}) => {
  const { t } = useLocalization();
  const { passSettings } = useStoreSettings();
  const navigate = useNavigate();

  return (
    <Box sx={{ textAlign: 'center', px: 4, py: 5 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#333',
          fontSize: '24px',
        }}
      >
        {t('auth.registerSuccess.title')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: '#666',
          mb: 4,
          lineHeight: 1.6,
          fontSize: '16px',
        }}
      >
        {t('auth.registerSuccess.subtitle')}
      </Typography>

      
      <Box
        sx={{
          borderRadius: 2,
          p: 3,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: passSettings?.backgroundColor,
          color: passSettings?.foregroundColor,
          mb: 3,
        }}
      >
        {passSettings?.backgroundUrl && (
          <Box
            component="img"
            src={passSettings.backgroundUrl}
            alt="Background"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.2,
            }}
          />
        )}
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            {passSettings?.logoUrl && (
              <Box component="img" src={passSettings.logoUrl} alt="Logo" sx={{ height: 32, width: 32, objectFit: 'contain' }} />
            )}
            {passSettings?.stripUrl && (
              <Box component="img" src={passSettings.stripUrl} alt="Strip" sx={{ height: 32, width: 64, objectFit: 'contain' }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.8, color: passSettings?.labelColor, display: 'block', mb: 1.5 }}>
            {passSettings?.heroText || "WALLET CARD"}
          </Typography>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: passSettings?.labelColor, display: 'block' }}>
              {passSettings?.lblMemberName}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>John Doe</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <Box>
              <Typography variant="caption" sx={{ color: passSettings?.labelColor, display: 'block' }}>
                {passSettings?.lblMembershipLvl}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Gold</Typography>
            </Box>
            {passSettings?.showPoints && (
              <Box>
                <Typography variant="caption" sx={{ color: passSettings?.labelColor, display: 'block' }}>
                  {passSettings?.lblPoints}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>1,250</Typography>
              </Box>
            )}
          </Box>
          {passSettings?.showLastUpdate && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="caption" sx={{ color: passSettings?.labelColor }}>
                {passSettings?.lblLastUpdate}: Jan 1, 2024
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Typography
        variant="h4"
        component="h2"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: '#333',
          fontSize: '20px',
        }}
      >
        {t('auth.registerSuccess.addToWalletPass')}
      </Typography>

      <Link type="button" component="button" sx={{ mb: 2.5 }} onClick={() => navigate(routes.addToWallet, { replace: true })}>
        <img src="/images/add-to-apple-wallet.png" alt="Add to Apple Wallet" width={142} />
      </Link>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#3d3d3d',
            mb: 0.5,
          }}
        >
          {t('auth.registerSuccess.androidDevice')}
        </Typography>
        <Link
          type="button"
          component="button"
          variant="body2"
          sx={{
            color: '#fc551d',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {t('auth.registerSuccess.pleaseSwitchHere')}
        </Link>
      </Box>


      {/* Continue Button */}
      <Button
        variant="text"
        size="large"
        onClick={handleContinue}
        sx={{ color: '#676767' }}
      >
        <span>{t('auth.registerSuccess.next')}</span>
        <ArrowForward sx={{ ml: 0.5 }} />
      </Button>
    </Box>
  )
}


export default function RegisterSuccess({ onContinue }: RegisterSuccessProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { storeSettings } = useStoreSettings();

  const handleContinue = () => {
    onContinue();
  };

  const handleClose = () => {
    onContinue();
  };

  // Mobile layout - Bottom panel
  if (!isDesktop) {
    return (
      <Box
        sx={{
          minHeight: '100svh',
          height: '100svh',
          position: 'relative',
          background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        {/* Bottom Panel */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'white',
            borderTopLeftRadius: '18px',
            borderTopRightRadius: '18px',
            borderBottomLeftRadius: '0px',
            borderBottomRightRadius: '0px',
            textAlign: 'center',
            width: '100%',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.1)',
          }}
        >
          <SuccessMessageContent handleContinue={handleContinue} />
        </Paper>
      </Box>
    );
  }

  // Desktop layout - Dialog
  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', backgroundColor: '#fff' }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>

        <SuccessMessageContent handleContinue={handleContinue} />
      </DialogContent>
    </Dialog>
  );
}
